/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import _ from 'lodash';
import Syphon from 'backbone.syphon';
import Backbone from 'backbone';

import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import UpdateUserProfile from '../../services/UpdateUserProfile';
import CustomerCommandsView from '../../../customer/views/CustomerCommandsView/CustomerCommandsView';

import tmplUserProfileView from './UserProfileView.hbs';
import './UserProfileView.css';

const UserProfileView = View.extend({
  template: tmplUserProfileView,

  className: 'user-profile',

  ui: {
    placeholder: '.placeholder',
    formInputs: '.form-inputs',
    commandsList: '#commands-list',
    customerField: '.customerField',
    actingAsCommandSelect: '.acting-as-command-select',
    intRoles: '.int-roles'
  },

  regions: {
    commandsList: '@ui.commandsList',
    intRolesCollection: '@ui.intRolesCollection'
  },

  childViewEvents: {
    'remove:command': 'getRemovedCommands'
  },

  onRender() {
    this.ui.customerField.hide();
    this.ui.intRoles.hide();

    this.removedCommands = [];

    const profile = this.model.get('profile');

    if (profile) {
      _.each(profile.commands, (command) => {
        if(command.id === profile.actingAsCommandId) {
          command.selected = true
        }
      })

      this.commandList = this.showChildView('commandsList', 
        new CustomerCommandsView({ 
          model: new Backbone.Model ({ 
            userId: profile.id, 
            commands: profile.commands,
            actingAsCommandId: profile.actingAsCommandId,
            isSelfService: true
          }) 
      }));

      this.ui.formInputs.show();
      this.ui.placeholder.hide();
    } else {
      this.ui.formInputs.hide();
      this.ui.placeholder.show();
    }

    this.listenTo(Radio.channel('userDetails'), 'update:profile', (data) => {
      this.showProfileData(data, data.isExternal);
    });
  },

  showProfileData(data, isExternal) {
    if (isExternal === '1') {
      $(this.ui.customerField).show();
    } else if (isExternal === '0') {
      $(this.ui.intRoles).show();
      this.checkAssignedRoles(data);
    }
    Syphon.deserialize(this.ui.formInputs, data);
  },

  checkAssignedRoles(data) {
    const assignedRoles = [];
    _.each(data.userRoles, (role) => {
      assignedRoles.push(role.internalUserType);
    });

    if (assignedRoles.includes('CRS')) data.CRS = true;
    if (assignedRoles.includes('EPC')) data.EPC = true;
    if (assignedRoles.includes('Admin')) data.Admin = true;
  },

  async onSave() {
    const formData = Syphon.serialize(this);
    formData.isExternal = this.model.get('profile').isExternal;
    formData.actingAsCommandId = this.commandList.customerCommandGridContainer.model.get('actingAsCommandId');

    const userData = await this.formatUserData(formData);

    try {
      if(userData.roles.length < 1 && formData.isExternal === '0') {
        showModal({
          title: 'Remove All Roles',
          view: new ConfirmationModalView({
            confirmText: 'Remove All Roles',
            model: new Backbone.Model({
              message: `Are you sure you want to remove all internal roles from ${userData.firstName} ${userData.lastName}? This action will revoke all permissions associated with their account.`,
              onConfirm: async () => {
                await validateForm(this.ui.formInputs);
                await UpdateUserProfile(userData);
              },
              onConfirmDone: this.showSaveSuccess
            })
          })
        });
      } else {
        await validateForm(this.ui.formInputs);
        await UpdateUserProfile(userData);
        this.showSaveSuccess();
      }
    } catch(err) {
        showErrorMessage({
          errorCode: 'userEditedError',
          errorTitle: 'User Edit Error',
          hideAfter: 10,
          errorMessage: 'Unable to update user\'s profile. Please try again or submit a Service Now ticket for assistance.',
        });
    }
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'userEdited',
      title: 'User Edited Successfully',
      message: 'The user\'s profile has been edited successfully.',
      hideAfter: 10
    });
  },

  async formatUserData(formData) {
    const roles = [
      ...(formData.CRS ? ['1'] : []),
      ...(formData.EPC ? ['2'] : []),
      ...(formData.Admin ? ['3'] : []),
    ]
    const userData = {
      id: this.model.get('profile').id,
      otdsUserId: formData.otdsUserId,
      isExternal: formData.isExternal,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      telephone: formData.telephone,
      isCACLogin: formData.isCACLogin,
      isAuthorized: formData.isAuthorized,
      actingAsCommandId: formData.actingAsCommandId,
      roles
    }

    return userData;
  },

  getRemovedCommands(commandId) {
    this.removedCommands.push(commandId);
  }
});

export default UserProfileView;
